import type { CheckoutSubMode } from './checkoutApi.js';
import { isMobileBankQrAllowedForCheckoutMode } from './checkoutModeUi.js';

export type CheckoutModePaymentChannel = 'mobile' | 'kiosk' | 'shop';

export interface CheckoutModeVisual {
  paymentChannel: CheckoutModePaymentChannel;
  /** Short label shown in the icon circle (accessibility: full title is in i18n). */
  iconLabel: string;
}

export type CheckoutModeDetailField = 'title' | 'description' | 'badge';

export type CheckoutModeAtKioskOptions = {
  readonly atKiosk?: boolean;
  /** Effective collect timing — PREPAY + usable NOW uses collectNow copy (Buy Again). */
  readonly collectTiming?: 'NOW' | 'LATER' | null;
};

/** PAY_NOW modes that still say “kiosk” in default modeDetail — need on-site variants. */
const MODES_WITH_ONSITE_MODE_DETAIL: ReadonlySet<CheckoutSubMode> = new Set([
  'PAY_NOW_SELF_SERVICE',
  'PAY_NOW_STAFF_HANDOFF',
]);

const MODE_VISUAL: Record<CheckoutSubMode, CheckoutModeVisual> = {
  PREPAY_COLLECT_LATER: { paymentChannel: 'mobile', iconLabel: 'QR' },
  PAY_NOW_SELF_SERVICE: { paymentChannel: 'kiosk', iconLabel: 'Ki' },
  PAY_NOW_STAFF_HANDOFF: { paymentChannel: 'kiosk', iconLabel: 'Ob' },
};

/** Shop / on-site chrome when `surface_kiosk` is Off (fail-closed). */
const MODE_VISUAL_ONSITE: Readonly<Partial<Record<CheckoutSubMode, CheckoutModeVisual>>> = {
  PAY_NOW_SELF_SERVICE: { paymentChannel: 'shop', iconLabel: 'Sh' },
  PAY_NOW_STAFF_HANDOFF: { paymentChannel: 'shop', iconLabel: 'Ob' },
};

/**
 * Resolve modeDetail i18n key. Fail-closed: when `atKiosk` is false, use shop/app
 * (`onSite`) variants for PAY_NOW modes that otherwise hardcode “kiosk”.
 * G6 — PREPAY + collect NOW uses collectNow.* so copy matches effective timing.
 */
export function resolveCheckoutModeDetailI18nKey(
  mode: CheckoutSubMode,
  field: CheckoutModeDetailField,
  options: CheckoutModeAtKioskOptions = {},
): string {
  const atKiosk = options.atKiosk === true;
  if (mode === 'PREPAY_COLLECT_LATER' && options.collectTiming === 'NOW') {
    return `checkout.modeDetail.PREPAY_COLLECT_LATER.collectNow.${field}`;
  }
  if (!atKiosk && MODES_WITH_ONSITE_MODE_DETAIL.has(mode)) {
    return `checkout.modeDetail.${mode}.onSite.${field}`;
  }
  return `checkout.modeDetail.${mode}.${field}`;
}

/**
 * Mode summary icon chrome. Fail-closed: when `atKiosk` is false, PAY_NOW modes
 * use shop/on-site channel + label (not kiosk / Ki).
 */
export function getCheckoutModeVisual(
  mode: CheckoutSubMode,
  options: CheckoutModeAtKioskOptions = {},
): CheckoutModeVisual {
  const atKiosk = options.atKiosk === true;
  if (!atKiosk && MODES_WITH_ONSITE_MODE_DETAIL.has(mode)) {
    return MODE_VISUAL_ONSITE[mode] ?? MODE_VISUAL[mode];
  }
  return MODE_VISUAL[mode];
}

export function getCheckoutModePaymentChannel(
  mode: CheckoutSubMode,
  options: CheckoutModeAtKioskOptions = {},
): CheckoutModePaymentChannel {
  return getCheckoutModeVisual(mode, options).paymentChannel;
}

export function checkoutModeSupportsMobilePayment(mode: CheckoutSubMode): boolean {
  return isMobileBankQrAllowedForCheckoutMode(mode);
}
